/**
 * Shared rules for the gallery / post media pickers.
 *
 * Posts and updates accept photos plus short videos. Videos are stored in S3 untouched
 * (no transcode, no server-side poster frame), so what the user picks is what everyone
 * plays — which is why the duration, size and format checks all live here, on the way in.
 *
 * Note on iPhone footage: clips straight from Photos are usually HEVC in a .mov container.
 * Safari and the Joyya iOS app play those fine; other browsers vary. We accept them anyway
 * (rejecting would block most real uploads) and MediaTile shows an honest fallback if the
 * viewer's browser turns out not to be able to decode one.
 *
 * Since the duration probe below is a real decode attempt, a browser that genuinely can't
 * read a clip will reject it here at pick time rather than uploading something it can't show.
 */

export const MAX_VIDEO_DURATION_SECONDS = 10;

/**
 * A clip a user trimmed to "10 seconds" commonly reports 10.03s. Reject only past the
 * tolerance, and keep this in lockstep with MAX_VIDEO_DURATION_SECONDS /
 * DURATION_TOLERANCE_SECONDS in backend/src/graphql/Image.ts — the server re-checks the
 * duration we report, so a stricter server means confusing mid-upload failures.
 */
export const DURATION_TOLERANCE_SECONDS = 0.5;

/** 10s of 4K HEVC lands around 50-60MB; this leaves headroom without allowing a stray feature film. */
export const MAX_VIDEO_BYTES = 100 * 1024 * 1024;

export const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime'];

/** `.mov` is listed explicitly because some browsers report an empty type for it. */
export const FILE_INPUT_ACCEPT = 'image/*,video/mp4,video/quicktime,.mov,.mp4';

const PROBE_TIMEOUT_MS = 15000;

export const isVideoContentType = (contentType?: string | null): boolean =>
    !!contentType && contentType.startsWith('video/');

/** Falls back to the extension for browsers that hand us a blank `File.type` on .mov. */
export const videoTypeForFile = (file: File): string | null => {
    if (isVideoContentType(file.type)) return file.type;
    if (/\.mov$/i.test(file.name)) return 'video/quicktime';
    if (/\.mp4$/i.test(file.name)) return 'video/mp4';
    return null;
};

export const isVideoFile = (file: File): boolean => videoTypeForFile(file) !== null;

/**
 * The contentType to send to `createImageUpload`, or undefined to leave it off.
 *
 * Photos deliberately send nothing: the backend then signs the upload as
 * application/octet-stream exactly as it always has, so heic/webp/gif keep working
 * instead of hitting the video-era allowlist.
 */
export const uploadContentTypeForFile = (file: File): string | undefined =>
    videoTypeForFile(file) ?? undefined;

/**
 * The Content-Type header to PUT with.
 *
 * This is what actually determines the stored object's content type: the presign is
 * generated with SignedHeaders=host only, so the ContentType the backend passes to
 * PutObjectCommand never reaches S3 — the header sent here does. Getting it wrong means a
 * video is stored as application/octet-stream and browsers refuse to play it.
 *
 * Keep it equal to `uploadContentTypeForFile` regardless: if the presign is ever tightened
 * to sign content-type, a mismatch would start failing with an opaque 403.
 */
export const putContentTypeForFile = (file: File): string =>
    uploadContentTypeForFile(file) ?? 'application/octet-stream';

export const formatDuration = (seconds: number): string => {
    if (!Number.isFinite(seconds)) return '';
    const whole = Math.round(seconds);
    if (whole < 60) return `${whole}s`;
    return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};

export const formatBytes = (bytes: number): string => {
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(mb >= 10 ? 0 : 1)}MB` : `${Math.max(1, Math.round(bytes / 1024))}KB`;
};

/** Reads duration out of a metadata-only load. Always revokes the object URL. */
const probeVideoDuration = (file: File): Promise<number> =>
    new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const video = document.createElement('video');
        let settled = false;

        const finish = (act: () => void) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            video.onloadedmetadata = null;
            video.onerror = null;
            video.removeAttribute('src');
            video.load();
            URL.revokeObjectURL(objectUrl);
            act();
        };

        // Without this, a container the browser can't parse leaves the picker hanging.
        const timer = setTimeout(() => finish(() => reject(new Error('Timed out reading video'))), PROBE_TIMEOUT_MS);

        video.preload = 'metadata';
        video.muted = true;
        video.onloadedmetadata = () => {
            const { duration } = video;
            finish(() => {
                if (Number.isFinite(duration) && duration > 0) resolve(duration);
                else reject(new Error('Could not read video duration'));
            });
        };
        video.onerror = () => finish(() => reject(new Error('Could not decode video')));
        video.src = objectUrl;
    });

export type MediaValidation =
    | { ok: true; durationSeconds?: number }
    | { ok: false; reason: string };

/**
 * Validates one picked file. Resolves a reason rather than throwing so callers can
 * stage the files that passed and report the ones that didn't, in one pass.
 */
export const validateMediaFile = async (file: File): Promise<MediaValidation> => {
    const videoType = videoTypeForFile(file);

    if (!videoType) {
        return file.type.startsWith('image/')
            ? { ok: true }
            : { ok: false, reason: `${file.name} isn't an image or a video.` };
    }

    if (!ACCEPTED_VIDEO_TYPES.includes(videoType)) {
        return { ok: false, reason: `${file.name} isn't a supported video format — use MP4 or MOV.` };
    }

    if (file.size > MAX_VIDEO_BYTES) {
        return {
            ok: false,
            reason: `${file.name} is ${formatBytes(file.size)} — videos must be under ${formatBytes(MAX_VIDEO_BYTES)}.`,
        };
    }

    let durationSeconds: number;
    try {
        durationSeconds = await probeVideoDuration(file);
    } catch {
        // Includes HEVC on some browsers, so keep the wording non-committal.
        return { ok: false, reason: `Couldn't read ${file.name}. It may be an unsupported video format.` };
    }

    if (durationSeconds > MAX_VIDEO_DURATION_SECONDS + DURATION_TOLERANCE_SECONDS) {
        return {
            ok: false,
            reason: `${file.name} is ${formatDuration(durationSeconds)} — videos must be ${MAX_VIDEO_DURATION_SECONDS} seconds or shorter.`,
        };
    }

    return { ok: true, durationSeconds };
};

/** Runs validateMediaFile over a picked batch, splitting passes from rejection messages. */
export const validateMediaFiles = async (
    files: File[],
): Promise<{ accepted: { file: File; durationSeconds?: number }[]; errors: string[] }> => {
    const accepted: { file: File; durationSeconds?: number }[] = [];
    const errors: string[] = [];

    for (const file of files) {
        const result = await validateMediaFile(file);
        if (result.ok) accepted.push({ file, durationSeconds: result.durationSeconds });
        else errors.push(result.reason);
    }

    return { accepted, errors };
};
